import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import MaterialsComparison from "./components/MaterialsComparison";
import ConfigurationVisualiser from "./components/ConfigurationVisualiser";

const features = [
  { icon: "ri-sun-line", title: "Maximum Natural Light", desc: "Slim 45mm sightlines mean up to 80% more glass than conventional doors, flooding your home with daylight." },
  { icon: "ri-shield-check-line", title: "A-Rated Energy Efficiency", desc: "Thermally broken aluminium frames and double or triple glazing keep warmth in and energy bills down." },
  { icon: "ri-lock-2-line", title: "Secured by Design", desc: "Multi-point locking as standard with anti-lift bolts, hook bolts, and PAS 24 security glazing." },
  { icon: "ri-palette-line", title: "Any RAL Colour", desc: "Powder-coated in any RAL colour — from classic Anthracite Grey to custom shades to match your home." },
  { icon: "ri-tools-line", title: "Made to Measure", desc: "Every set is precisely manufactured to your exact opening dimensions. No off-the-shelf compromises." },
  { icon: "ri-award-line", title: "10-Year Guarantee", desc: "Full 10-year structural and weathertight guarantee on all frames, glazing units, and hardware." },
];

const faqs = [
  { q: "How much do bifold doors cost in Surrey?", a: "Bifold door prices vary depending on size, number of panels, material, and specification. A 3-panel aluminium set typically starts from around £2,800 fully installed. uPVC bifold doors start from approximately £1,800. We provide free, no-obligation quotes with written pricing — call 07852 636 300 or complete our online form." },
  { q: "Aluminium or uPVC — which should I choose?", a: "Aluminium is the premium choice: slimmer frames, wider spans, more colour options, and a longer lifespan. uPVC is more budget-friendly and perfectly suitable for smaller openings. We cover both options in detail on this page, and our team will advise you during your free survey." },
  { q: "How long does installation take?", a: "Most bifold door installations are completed in a single day. Larger or more complex installations may take two days. We prepare the opening on day one if needed, and fit the door frame and glazing units to completion before we leave." },
  { q: "Do I need planning permission for bifold doors in Surrey?", a: "In most cases, replacing existing doors or windows with bifold doors falls under permitted development rights and does not require planning permission. If you are adding a new opening, planning permission may be required. We advise on this during your free survey across all Surrey areas." },
  { q: "How do I clean and maintain bifold doors?", a: "Aluminium bifold doors require very little maintenance. We recommend cleaning the frames with warm soapy water every few months and lubricating the rollers and hinges annually. The powder-coat finish will not peel, flake, or require repainting." },
  { q: "Are bifold doors draught-proof and weathertight?", a: "Our bifold doors use compression seals and brush seals throughout to provide excellent weathertight performance. They are tested to British Standards for air permeability, water tightness, and wind resistance." },
  { q: "What areas of Surrey do you cover?", a: "We install bifold doors throughout Surrey and South London, including Caterham, Redhill, Reigate, Oxted, Warlingham, Purley, Coulsdon, Banstead, Epsom, Leatherhead, Dorking, Guildford, and surrounding areas. Free home surveys available across all these locations." },
];

const processSteps = [
  { step: "1", title: "Free Home Survey", desc: "One of our surveyors visits your home, measures the opening precisely, and discusses your colour and configuration options." },
  { step: "2", title: "Written Quotation", desc: "We provide a clear, detailed written quotation within 48 hours — no hidden extras, no surprise costs." },
  { step: "3", title: "Manufacture & Delivery", desc: "Your doors are manufactured to your exact measurements, typically within 4–6 weeks, and delivered directly to site." },
  { step: "4", title: "Expert Installation", desc: "Our trained installation team fits your doors professionally, tidies up completely, and walks you through operation and care." },
];

const testimonials = [
  { name: "Sarah Thompson", location: "Caterham, Surrey", product: "4-Panel Aluminium Bifold", text: "The bifold doors are absolutely stunning. They have transformed our kitchen extension — we practically live outside in summer now. Ensure Windows were professional from first to last.", avatar: "ST" },
  { name: "Robert & Emma Finch", location: "Purley, South London", product: "3-Panel Aluminium Bifold", text: "We had a 3-panel set installed overlooking our garden. The difference in natural light is remarkable. The installation team were tidy, efficient, and clearly knew what they were doing.", avatar: "RF" },
  { name: "David Patel", location: "Warlingham, Surrey", product: "5-Panel Aluminium Bifold", text: "Competitive price, beautiful product, faultless installation. The Anthracite Grey matches our extension perfectly. Could not be happier.", avatar: "DP" },
  { name: "Claire & James Morton", location: "Redhill, Surrey", product: "uPVC 3-Panel Bifold", text: "We went for uPVC to keep costs down and we are delighted with the result. Great value, looks smart, and the team were brilliant throughout.", avatar: "CM" },
];

const surreyAreas = [
  "Caterham", "Redhill", "Reigate", "Oxted", "Warlingham", "Purley",
  "Coulsdon", "Banstead", "Epsom", "Leatherhead", "Dorking", "Guildford",
  "Woking", "Farnham", "Godalming", "Haslemere",
];

const galleryImages = [
  { src: "https://public.readdy.ai/ai/img_res/34de652c31928d11611a571ccb7f0f81.jpg", alt: "Anthracite bifold doors, Caterham Surrey", location: "Caterham", panels: "4 Panel" },
  { src: "https://readdy.ai/api/search-image?query=white%20aluminium%20bifold%20doors%20three%20panel%20modern%20uk%20house%20garden%20view%20open%20air%20natural%20light%20bright%20interior%20clean%20minimal&width=600&height=400&seq=bfg2&orientation=landscape", alt: "White bifold doors, Redhill Surrey", location: "Redhill", panels: "3 Panel" },
  { src: "https://readdy.ai/api/search-image?query=large%20six%20panel%20bifold%20doors%20open%20uk%20home%20wide%20garden%20access%20floor%20to%20ceiling%20glass%20minimal%20frame%20elegant%20contemporary&width=600&height=400&seq=bfg3&orientation=landscape", alt: "6 panel bifold doors, Oxted Surrey", location: "Oxted", panels: "6 Panel" },
  { src: "https://readdy.ai/api/search-image?query=bifold%20doors%20closed%20exterior%20view%20uk%20residential%20extension%20aluminium%20grey%20frame%20garden%20light%20evening%20modern%20home&width=600&height=400&seq=bfg4&orientation=landscape", alt: "Bifold doors exterior, Coulsdon Surrey", location: "Coulsdon", panels: "4 Panel" },
  { src: "https://readdy.ai/api/search-image?query=modern%20bifold%20sliding%20door%20interior%20view%20open%20plan%20living%20kitchen%20garden%20connection%20uk%20home%20contemporary%20light%20airy&width=600&height=400&seq=bfg5&orientation=landscape", alt: "Interior bifold doors view, Purley", location: "Purley", panels: "5 Panel" },
  { src: "https://readdy.ai/api/search-image?query=aluminium%20bifold%20doors%20corner%20installation%20uk%20home%20two%20sets%20meeting%20corner%20glass%20panoramic%20garden%20view%20luxury&width=600&height=400&seq=bfg6&orientation=landscape", alt: "Corner bifold doors, Warlingham Surrey", location: "Warlingham", panels: "3 Panel" },
];

export default function BifoldDoorsPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    town: "",
    material: "",
    panels: "",
    colour: "",
    message: "",
  });

  useEffect(() => {
    document.title = "Bifold Doors Surrey | Aluminium & uPVC Bifold Doors Caterham | Ensure Windows";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Premium aluminium and uPVC bifold doors installed across Surrey. Free home surveys in Caterham, Redhill, Reigate, Oxted & surrounding areas. 10-year guarantee. Call 07852 636 300.");
    return () => {
      document.title = "Ensure Windows Caterham | Premium Windows & Doors Surrey";
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await fetch("https://readdy.ai/api/form/d7ajpfu41prrpukdf8dg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(form as Record<string, string>).toString(),
      });
    } finally {
      setSubmitted(true);
      setSubmitting(false);
    }
  };

  return (
    <main>
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=luxury%20wide%20open%20aluminium%20bifold%20doors%20contemporary%20uk%20home%20garden%20extension%20large%20glass%20panels%20anthracite%20grey%20frame%20natural%20light%20lifestyle%20beautiful%20summer%20interior%20outdoor%20living&width=1920&height=900&seq=bf-hero-v2&orientation=landscape"
            alt="Aluminium bifold doors Surrey"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/20"></div>
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-24">
          <div className="max-w-2xl">
            <nav className="flex items-center gap-2 text-xs text-stone-400 mb-6" aria-label="Breadcrumb" style={{ fontFamily: "'Raleway', sans-serif" }}>
              <Link to="/" className="hover:text-amber-400 transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/products" className="hover:text-amber-400 transition-colors cursor-pointer">Products</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-white">Bifold Doors</span>
            </nav>
            <span className="inline-block text-amber-400 text-xs font-bold tracking-widest uppercase border border-amber-400/50 px-4 py-1.5 rounded-full mb-5" style={{ fontFamily: "'Raleway', sans-serif" }}>
              Caterham · Surrey · South London
            </span>
            <h1 className="text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight mb-5" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Bifold Doors in Surrey
            </h1>
            <p className="text-lg text-stone-200 leading-relaxed mb-4" style={{ fontFamily: "'Raleway', sans-serif" }}>
              Aluminium and uPVC bifold doors, custom-made and expertly installed for homes across Caterham, Surrey, and South London. Free surveys and written quotes from your local specialists.
            </p>
            <div className="flex flex-wrap gap-3 mb-8">
              {["Aluminium & uPVC", "2–6 Panel Configs", "200+ RAL Colours", "Free Surrey Survey"].map((tag) => (
                <span key={tag} className="text-xs text-stone-300 bg-white/10 border border-white/20 px-3 py-1.5 rounded-full" style={{ fontFamily: "'Raleway', sans-serif" }}>{tag}</span>
              ))}
            </div>
            <div className="flex flex-wrap gap-4">
              <a href="#quote-form" className="bg-amber-600 hover:bg-amber-500 text-white font-semibold px-8 py-4 rounded-md transition-colors whitespace-nowrap cursor-pointer" style={{ fontFamily: "'Raleway', sans-serif" }}>
                Get a Free Quote
              </a>
              <a href="#materials" className="border border-white/60 hover:border-white text-white hover:bg-white/10 font-semibold px-8 py-4 rounded-md transition-all whitespace-nowrap cursor-pointer" style={{ fontFamily: "'Raleway', sans-serif" }}>
                Compare Materials
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Quick stats */}
      <section className="bg-zinc-900 py-8">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-5 gap-4 divide-x divide-white/10">
          {[
            { icon: "ri-ruler-2-line", v: "45mm", l: "Slim Sightlines" },
            { icon: "ri-award-line", v: "10 Year", l: "Guarantee" },
            { icon: "ri-palette-line", v: "200+", l: "RAL Colours" },
            { icon: "ri-calendar-check-line", v: "4–6 Wks", l: "Lead Time" },
            { icon: "ri-map-pin-2-line", v: "Surrey", l: "Local Installer" },
          ].map((b) => (
            <div key={b.l} className="flex flex-col items-center text-center px-4">
              <div className="w-7 h-7 flex items-center justify-center mb-2"><i className={`${b.icon} text-amber-400 text-lg`}></i></div>
              <span className="text-white text-xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{b.v}</span>
              <span className="text-stone-400 text-xs mt-0.5" style={{ fontFamily: "'Raleway', sans-serif" }}>{b.l}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>About Bifold Doors</span>
            <h2 className="text-4xl font-bold text-zinc-900 mt-3 mb-5" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Transform Your Home with Bifold Doors
            </h2>
            <div className="space-y-4 text-zinc-500 text-sm leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif" }}>
              <p>
                Bifold doors have become the most popular way for Surrey homeowners to connect their living space with the garden. Whether you choose <strong>aluminium</strong> for the slimmest frames and widest spans, or <strong>uPVC</strong> for a more budget-conscious upgrade, the result is a dramatic transformation in light, space, and lifestyle.
              </p>
              <p>
                As a local installer based in Caterham, we supply and fit bifold doors throughout Surrey — from Redhill and Reigate to Oxted, Warlingham, Purley, and beyond. Every set is <strong>made to measure</strong> for your exact opening, and every installation is backed by our 10-year guarantee.
              </p>
              <p>
                Use this page to compare aluminium vs uPVC, explore panel configurations from 2 to 6 panels, and get a free quote tailored to your Surrey home.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 mt-7">
              <a href="#materials" className="inline-flex items-center gap-2 bg-zinc-900 text-white text-sm font-semibold px-5 py-3 rounded-md hover:bg-zinc-800 transition-colors whitespace-nowrap cursor-pointer" style={{ fontFamily: "'Raleway', sans-serif" }}>
                Compare Materials <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-down-line"></i></div>
              </a>
              <a href="#configurations" className="inline-flex items-center gap-2 border border-stone-300 text-zinc-700 text-sm font-semibold px-5 py-3 rounded-md hover:border-zinc-500 transition-colors whitespace-nowrap cursor-pointer" style={{ fontFamily: "'Raleway', sans-serif" }}>
                View Configurations <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-down-line"></i></div>
              </a>
            </div>
          </div>
          <div className="w-full h-[440px] rounded-xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=aluminium%20bifold%20door%20close%20up%20slim%20frame%20detail%20precision%20engineering%20anthracite%20grey%20glass%20modern%20uk%20home%20quality%20craftsmanship%20interior%20light&width=700&height=440&seq=bf-intro-v2&orientation=landscape"
              alt="Slim aluminium bifold door frames Surrey"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* Materials Comparison */}
      <MaterialsComparison />

      {/* Configuration Visualiser */}
      <ConfigurationVisualiser />

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>Specification</span>
            <h2 className="text-4xl font-bold text-zinc-900 mt-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Features &amp; Technical Specification
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f) => (
              <div key={f.title} className="flex items-start gap-4 p-6 rounded-xl border border-stone-100">
                <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center bg-amber-50 rounded-lg">
                  <i className={`${f.icon} text-amber-600 text-xl`}></i>
                </div>
                <div>
                  <h3 className="text-base font-bold text-zinc-800 mb-1" style={{ fontFamily: "'Raleway', sans-serif" }}>{f.title}</h3>
                  <p className="text-zinc-500 text-sm leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif" }}>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section id="gallery" className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>Our Work</span>
            <h2 className="text-4xl font-bold text-zinc-900 mt-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Bifold Door Installations Across Surrey
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {galleryImages.map((img) => (
              <div key={img.src} className="relative w-full h-52 md:h-64 rounded-xl overflow-hidden group cursor-pointer">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="absolute bottom-3 left-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  <p className="text-white text-xs font-semibold" style={{ fontFamily: "'Raleway', sans-serif" }}>{img.location} · {img.panels}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/gallery" className="inline-flex items-center gap-2 text-amber-600 font-semibold hover:gap-3 transition-all cursor-pointer" style={{ fontFamily: "'Raleway', sans-serif" }}>
              View Full Project Gallery <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line"></i></div>
            </Link>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>How It Works</span>
            <h2 className="text-4xl font-bold text-zinc-900 mt-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Our Simple 4-Step Process</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((s, i) => (
              <div key={s.step} className="relative text-center">
                {i < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-[calc(50%+2rem)] right-0 border-t-2 border-dashed border-amber-200 z-0"></div>
                )}
                <div className="relative z-10 w-16 h-16 flex items-center justify-center bg-amber-600 rounded-full mx-auto mb-5 text-white text-2xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  {s.step}
                </div>
                <h3 className="text-lg font-bold text-zinc-800 mb-2" style={{ fontFamily: "'Raleway', sans-serif" }}>{s.title}</h3>
                <p className="text-zinc-500 text-sm leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif" }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>Customer Reviews</span>
            <h2 className="text-4xl font-bold text-zinc-900 mt-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              What Our Bifold Door Customers Say
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {testimonials.map((t) => (
              <div key={t.name} className="bg-white rounded-xl p-6 border border-stone-100">
                <div className="flex gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill text-amber-500 text-sm"></i></div>
                  ))}
                </div>
                <span className="inline-block text-xs bg-amber-50 text-amber-700 px-2.5 py-1 rounded-full mb-3" style={{ fontFamily: "'Raleway', sans-serif" }}>{t.product}</span>
                <p className="text-zinc-600 text-sm leading-relaxed mb-5 italic" style={{ fontFamily: "'Raleway', sans-serif" }}>&ldquo;{t.text}&rdquo;</p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-amber-600 rounded-full text-white text-xs font-bold">{t.avatar}</div>
                  <div>
                    <p className="text-zinc-800 text-sm font-semibold" style={{ fontFamily: "'Raleway', sans-serif" }}>{t.name}</p>
                    <p className="text-zinc-400 text-xs" style={{ fontFamily: "'Raleway', sans-serif" }}>{t.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Surrey Coverage */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>Coverage Area</span>
          <h2 className="text-3xl font-bold text-zinc-900 mt-3 mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Bifold Door Installations Across Surrey &amp; South London
          </h2>
          <p className="text-zinc-500 text-sm mb-8 max-w-xl mx-auto" style={{ fontFamily: "'Raleway', sans-serif" }}>
            Based in Caterham, we offer free home surveys and professional bifold door installation throughout Surrey and South London.
          </p>
          <div className="flex flex-wrap justify-center gap-2.5">
            {surreyAreas.map((area) => (
              <span key={area} className="text-sm bg-stone-50 border border-stone-200 text-zinc-600 px-4 py-2 rounded-full" style={{ fontFamily: "'Raleway', sans-serif" }}>
                <i className="ri-map-pin-2-line text-amber-500 mr-1.5 text-xs"></i>{area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-amber-600 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>FAQ</span>
            <h2 className="text-4xl font-bold text-zinc-900 mt-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-stone-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer bg-white hover:bg-stone-50 transition-colors"
                >
                  <span className="font-semibold text-zinc-800 text-sm pr-4" style={{ fontFamily: "'Raleway', sans-serif" }}>{faq.q}</span>
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className={`${openFaq === i ? "ri-subtract-line" : "ri-add-line"} text-amber-600`}></i>
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 bg-stone-50">
                    <p className="text-zinc-500 text-sm leading-relaxed" style={{ fontFamily: "'Raleway', sans-serif" }}>{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Form */}
      <section id="quote-form" className="py-20" style={{ background: "linear-gradient(135deg, #1a1a1a 0%, #2d1f0e 100%)" }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-14 items-start">
            {/* Left info */}
            <div>
              <span className="text-amber-400 text-xs font-bold tracking-widest uppercase" style={{ fontFamily: "'Raleway', sans-serif" }}>Free Surrey Survey</span>
              <h2 className="text-4xl font-bold text-white mt-4 mb-5" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Get Your Free Bifold Door Quote in Surrey
              </h2>
              <p className="text-stone-300 text-sm leading-relaxed mb-8" style={{ fontFamily: "'Raleway', sans-serif" }}>
                Tell us about your project and we&apos;ll arrange a free home survey at a time that suits you. We cover all areas across Surrey and South London — from Caterham and Redhill to Guildford and beyond.
              </p>

              {/* Contact options */}
              <div className="flex flex-col gap-4 mb-8">
                {[
                  { icon: "ri-phone-line", text: "07852 636 300", href: "tel:07852636300", label: "Call us" },
                  { icon: "ri-mail-line", text: "sales@ensurewindows.co.uk", href: "mailto:sales@ensurewindows.co.uk", label: "Email us" },
                  { icon: "ri-map-pin-2-line", text: "Seven House, Town End, Caterham CR3 5UG", href: "#", label: "Visit us" },
                ].map((item) => (
                  <a key={item.text} href={item.href} className="flex items-center gap-3 cursor-pointer group">
                    <div className="w-9 h-9 flex items-center justify-center bg-amber-600/20 rounded-md flex-shrink-0">
                      <i className={`${item.icon} text-amber-400`}></i>
                    </div>
                    <div>
                      <p className="text-stone-500 text-xs" style={{ fontFamily: "'Raleway', sans-serif" }}>{item.label}</p>
                      <span className="text-stone-300 group-hover:text-amber-400 transition-colors text-sm" style={{ fontFamily: "'Raleway', sans-serif" }}>{item.text}</span>
                    </div>
                  </a>
                ))}
              </div>

              {/* Why choose us bullets */}
              <div className="border-t border-white/10 pt-7">
                <p className="text-stone-400 text-xs uppercase tracking-wide mb-4" style={{ fontFamily: "'Raleway', sans-serif" }}>Why choose Ensure Windows?</p>
                <ul className="space-y-2.5">
                  {[
                    "Free, no-obligation home survey across Surrey",
                    "Written quotation within 48 hours",
                    "Aluminium and uPVC options available",
                    "FENSA registered installer",
                    "10-year guarantee on all installations",
                    "Local team based in Caterham since 2008",
                  ].map((p) => (
                    <li key={p} className="flex items-start gap-2.5">
                      <div className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-amber-400 text-sm"></i>
                      </div>
                      <span className="text-stone-300 text-sm" style={{ fontFamily: "'Raleway', sans-serif" }}>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Form */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8">
              {submitted ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 flex items-center justify-center bg-amber-600/20 rounded-full mx-auto mb-4">
                    <i className="ri-check-line text-amber-400 text-3xl"></i>
                  </div>
                  <h3 className="text-white text-2xl font-bold mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Thank You!</h3>
                  <p className="text-stone-300 text-sm" style={{ fontFamily: "'Raleway', sans-serif" }}>
                    We&apos;ll be in touch within 24 hours to arrange your free survey.
                  </p>
                </div>
              ) : (
                <>
                  <h3 className="text-white text-xl font-bold mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Request a Free Survey &amp; Quote</h3>
                  <p className="text-stone-400 text-xs mb-6" style={{ fontFamily: "'Raleway', sans-serif" }}>All fields marked * are required. We&apos;ll never share your details.</p>
                  <form onSubmit={handleSubmit} data-readdy-form id="bifold-quote-form" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-stone-400 text-xs mb-1.5 block" style={{ fontFamily: "'Raleway', sans-serif" }}>Full Name *</label>
                        <input
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          required
                          placeholder="e.g. John Smith"
                          className="w-full bg-white/10 border border-white/20 text-white placeholder-stone-500 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400"
                          style={{ fontFamily: "'Raleway', sans-serif" }}
                        />
                      </div>
                      <div>
                        <label className="text-stone-400 text-xs mb-1.5 block" style={{ fontFamily: "'Raleway', sans-serif" }}>Phone Number *</label>
                        <input
                          name="phone"
                          value={form.phone}
                          onChange={handleChange}
                          required
                          placeholder="e.g. 07700 900000"
                          className="w-full bg-white/10 border border-white/20 text-white placeholder-stone-500 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400"
                          style={{ fontFamily: "'Raleway', sans-serif" }}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-stone-400 text-xs mb-1.5 block" style={{ fontFamily: "'Raleway', sans-serif" }}>Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        placeholder="e.g. john@example.com"
                        className="w-full bg-white/10 border border-white/20 text-white placeholder-stone-500 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400"
                        style={{ fontFamily: "'Raleway', sans-serif" }}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-stone-400 text-xs mb-1.5 block" style={{ fontFamily: "'Raleway', sans-serif" }}>Town / Area *</label>
                        <select
                          name="town"
                          value={form.town}
                          onChange={handleChange}
                          required
                          className="w-full bg-white/10 border border-white/20 text-stone-300 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400"
                          style={{ fontFamily: "'Raleway', sans-serif" }}
                        >
                          <option value="">Select your area</option>
                          {surreyAreas.map((a) => <option key={a}>{a}</option>)}
                          <option>Other Surrey area</option>
                          <option>South London</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-stone-400 text-xs mb-1.5 block" style={{ fontFamily: "'Raleway', sans-serif" }}>Postcode</label>
                        <input
                          name="address"
                          value={form.address}
                          onChange={handleChange}
                          placeholder="e.g. CR3 5UG"
                          className="w-full bg-white/10 border border-white/20 text-white placeholder-stone-500 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400"
                          style={{ fontFamily: "'Raleway', sans-serif" }}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-stone-400 text-xs mb-1.5 block" style={{ fontFamily: "'Raleway', sans-serif" }}>Preferred Material</label>
                        <select
                          name="material"
                          value={form.material}
                          onChange={handleChange}
                          className="w-full bg-white/10 border border-white/20 text-stone-300 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400"
                          style={{ fontFamily: "'Raleway', sans-serif" }}
                        >
                          <option value="">Not sure yet</option>
                          <option>Aluminium</option>
                          <option>uPVC</option>
                          <option>Need advice</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-stone-400 text-xs mb-1.5 block" style={{ fontFamily: "'Raleway', sans-serif" }}>Number of Panels</label>
                        <select
                          name="panels"
                          value={form.panels}
                          onChange={handleChange}
                          className="w-full bg-white/10 border border-white/20 text-stone-300 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400"
                          style={{ fontFamily: "'Raleway', sans-serif" }}
                        >
                          <option value="">Not sure yet</option>
                          <option>2 Panel</option>
                          <option>3 Panel</option>
                          <option>4 Panel</option>
                          <option>5 Panel</option>
                          <option>6 Panel</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="text-stone-400 text-xs mb-1.5 block" style={{ fontFamily: "'Raleway', sans-serif" }}>Frame Colour (if known)</label>
                      <input
                        name="colour"
                        value={form.colour}
                        onChange={handleChange}
                        placeholder="e.g. Anthracite Grey RAL 7016"
                        className="w-full bg-white/10 border border-white/20 text-white placeholder-stone-500 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400"
                        style={{ fontFamily: "'Raleway', sans-serif" }}
                      />
                    </div>

                    <div>
                      <label className="text-stone-400 text-xs mb-1.5 block" style={{ fontFamily: "'Raleway', sans-serif" }}>Project Details</label>
                      <textarea
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        rows={3}
                        maxLength={500}
                        placeholder="Tell us about your opening size, project type (extension, replacement, etc.), or any questions..."
                        className="w-full bg-white/10 border border-white/20 text-white placeholder-stone-500 text-sm px-4 py-3 rounded-md focus:outline-none focus:border-amber-400 resize-none"
                        style={{ fontFamily: "'Raleway', sans-serif" }}
                      />
                      <p className="text-stone-500 text-xs mt-1 text-right" style={{ fontFamily: "'Raleway', sans-serif" }}>{form.message.length}/500</p>
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-amber-600 hover:bg-amber-500 text-white font-semibold py-4 rounded-md transition-colors whitespace-nowrap cursor-pointer text-sm"
                      style={{ fontFamily: "'Raleway', sans-serif" }}
                    >
                      {submitting ? "Sending your enquiry..." : "Request Free Survey & Quote"}
                    </button>
                    <p className="text-stone-500 text-xs text-center" style={{ fontFamily: "'Raleway', sans-serif" }}>
                      Free survey · No obligation · Response within 24 hours
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
